<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useVModel, useDropZone } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    modelValue?: File[]
    defaultValue?: File[]
    label?: string
    buttonLabel?: string
    additionalLabel?: string
    maxFileSize?: number
    accept?: string[]
    disabled?: boolean
    multiple?: boolean
    maxFiles?: number
  }>(),
  {
    class: undefined,
    modelValue: () => [],
    defaultValue: () => [],
    label: 'Upload File',
    buttonLabel: 'Pilih File',
    additionalLabel: 'Letakkan file kamu di sini atau',
    maxFileSize: 10 * 1024 * 1024,
    maxFiles: 0, // 0 means no limit
    accept: () => [
      '.pdf',
      '.xls',
      '.xlsx',
      '.doc',
      '.docx',
      '.jpg',
      '.jpeg',
      '.png'
    ]
  }
)

const emits = defineEmits<{
  (e: 'update:modelValue', payload: File): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  defaultValue: props.defaultValue
})

const dropZoneRef = useTemplateRef('dropZoneRef')
const fileRef = useTemplateRef('fileRef')

const ableToUpload = computed(() => {
  if (props.disabled) return false
  if (props.maxFiles && modelValue.value.length >= props.maxFiles) return false
  return true
})

const openFile = () => {
  if (!ableToUpload.value) return
  fileRef.value?.click()
}

const validateFile = (file: File) => {
  if (!props.accept.includes('.' + (file.name.split('.')?.pop() ?? 'null')))
    return `Format file yang bisa diunggah adalah ${props.accept
      .map((item, index) => {
        if (index !== props.accept.length - 1)
          return item.substring(1).toUpperCase()
        return 'atau ' + item.substring(1).toUpperCase()
      })
      .join(', ')}.`

  if (file.size > props.maxFileSize)
    return `Ukuran file tidak boleh lebih dari ${
      props.maxFileSize / 1024 / 1024
    } MB.`

  return null
}

const performUploadFile = (file?: File) => {
  if (!file) return

  const validateMessage = validateFile(file)
  if (validateMessage) return

  modelValue.value.push(file)
}

const onChange = () => {
  if (!fileRef.value) return

  for (const file of fileRef.value.files ?? []) {
    if (!ableToUpload.value) break
    performUploadFile(file)
  }

  fileRef.value.value = ''
}

const onDrop = (files: File[] | null) => {
  for (const file of files ?? []) {
    if (!ableToUpload.value) break
    performUploadFile(file)
  }
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  multiple: props.multiple,
  preventDefaultForUnhandled: false
})
</script>

<template>
  <div
    ref="dropZoneRef"
    :class="
      cn(
        'w-full rounded-xl border border-outline-variant bg-background relative',
        [modelValue.length > 0 ? 'border-solid' : 'border-dashed'],
        props.class
      )
    "
  >
    <div
      v-if="modelValue.length > 0 && !isOverDropZone"
      class="flex flex-col gap-4 p-4"
    >
      <div class="flex items-center justify-between gap-4">
        <p class="font-semibold text-sm">
          Uploaded Files ({{ modelValue.length }})
        </p>
        <UiButton
          class="border-none rounded-md"
          size="xs"
          variant="errorOutline"
          icon="material-symbols:delete"
          type="button"
          @click="modelValue = []"
        />
      </div>
      <div class="flex flex-col gap-2">
        <UiAttachmentChipAttachment
          v-for="(file, index) in modelValue"
          :key="index"
          :type="findFileType(file.name)"
          :label="file.name"
          @dismiss="modelValue.splice(index, 1)"
        />
      </div>
      <UiButton
        v-if="ableToUpload"
        :label="buttonLabel"
        size="sm"
        variant="outline"
        icon="material-symbols:upload"
        :disabled="props.disabled"
        type="button"
        @click="openFile"
      />
    </div>
    <div
      v-else
      class="flex flex-col justify-center items-center p-5 gap-2.5 min-h-40"
    >
      <Icon v-if="isOverDropZone" name="line-md:file-upload" size="33px" />
      <Icon v-else name="line-md:file" size="33px" />
      <p class="text-sm font-semibold text-center">
        {{ isOverDropZone ? 'Letakkan disini...' : label }}
      </p>
      <p
        v-if="!isOverDropZone"
        class="text-xs text-center text-on-surface-variant"
      >
        {{ additionalLabel }}
      </p>
      <div v-if="!isOverDropZone" class="flex">
        <UiButton
          :label="buttonLabel"
          size="sm"
          variant="outline"
          icon="material-symbols:upload"
          :disabled="props.disabled"
          type="button"
          @click="openFile"
        />
      </div>
    </div>

    <input
      ref="fileRef"
      type="file"
      :class="
        modelValue.length > 0
          ? 'hidden'
          : 'absolute inset-0 cursor-pointer opacity-0'
      "
      :accept="props.accept.join(',')"
      :multiple="props.multiple"
      :disabled="props.disabled"
      @change="onChange"
    />
  </div>
</template>
